import { useState, useEffect, useCallback } from "react";
import { findAllCompanyForBulkEmail } from "@/apis/companyStamp";
import { CompanyStampListItemDto } from "@/dto/CompanyStampDto";
import { createFilterOptions } from "@mui/material/Autocomplete";

/**
 * 대량 이메일용 도장 목록 관리 커스텀 훅
 * @description 도장 목록 조회, 선택, 검색 로직을 관리합니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
export const useCompanySelection = () => {
  // 상태 관리
  const [doJangList, setDoJangList] = useState<CompanyStampListItemDto[]>([]);
  const [isLoadingDoJang, setIsLoadingDoJang] = useState(true);
  const [selectedDoJangId, setSelectedDoJangId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  /**
   * 도장 목록 조회 함수
   */
  const fetchDoJangList = useCallback(async () => {
    try {
      setIsLoadingDoJang(true);
      setError(null);

      console.log("[DOJANG_FETCH_START]", {
        message: "도장 목록 전체 조회 시작 (새로운 API 사용)",
        timestamp: new Date().toISOString(),
      });

      // 새로운 대량 이메일용 API로 모든 도장 데이터 가져오기
      const response = await findAllCompanyForBulkEmail();

      console.log("[DOJANG_LIST_RESPONSE]", {
        total: response.count,
        page: response.page,
        limit: response.limit,
        listLength: response.list.length,
        firstFew: response.list.slice(0, 3).map((d) => ({
          comId: d.comId,
          comNm: d.comNm,
          admNm: d.admNm,
        })),
        lastFew: response.list.slice(-3).map((d) => ({
          comId: d.comId,
          comNm: d.comNm,
          admNm: d.admNm,
        })),
        timestamp: new Date().toISOString(),
      });

      // 데이터 검증
      if (!response.list || !Array.isArray(response.list)) {
        throw new Error("Invalid response format: list is not an array");
      }

      setDoJangList(response.list);

      // 전체 도장 수가 현재 가져온 것보다 많으면 경고 표시
      if (response.count > response.list.length) {
        console.warn("[DOJANG_LIST_WARNING]", {
          total: response.count,
          fetched: response.list.length,
          message: "일부 도장만 조회됨 - API 제한이 있을 수 있습니다.",
        });
      }

      return {
        success: true,
        count: response.count,
        fetchedCount: response.list.length,
      };
    } catch (error: any) {
      console.error("[ERROR] 도장 목록 조회 실패:", {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });

      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "도장 목록 조회에 실패했습니다.";
      setError(errorMessage);
      setDoJangList([]);

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoadingDoJang(false);
    }
  }, []);

  /**
   * 도장 선택 처리 함수
   */
  const handleDoJangSelection = useCallback((doJangId: string) => {
    console.log("[DOJANG_SELECTION]", {
      selectedId: doJangId,
      timestamp: new Date().toISOString(),
    });
    setSelectedDoJangId(doJangId);
  }, []);

  /**
   * 선택된 도장 객체 반환
   */
  const selectedDoJang =
    doJangList.find((dojang) => dojang.comId === selectedDoJangId) || null;

  /**
   * 도장 검색 필터 함수 (MUI Autocomplete용)
   */
  const filterDoJangOptions = createFilterOptions<CompanyStampListItemDto>({
    matchFrom: "any",
    limit: 1000, // 클라이언트 측 필터링 시 최대 옵션 수
    stringify: (option) =>
      `${option.comNm} ${option.comId} ${option.admNm || ""}`,
  });

  /**
   * 컴포넌트 마운트 시 도장 목록 조회
   */
  useEffect(() => {
    fetchDoJangList();
  }, [fetchDoJangList]);

  /**
   * 도장 목록 재조회 함수
   */
  const refetchDoJangList = useCallback(() => {
    return fetchDoJangList();
  }, [fetchDoJangList]);

  /**
   * 선택 초기화 함수
   */
  const clearSelection = useCallback(() => {
    setSelectedDoJangId("");
  }, []);

  return {
    // 상태
    doJangList,
    isLoadingDoJang,
    selectedDoJangId,
    selectedDoJang,
    error,

    // 함수
    handleDoJangSelection,
    filterDoJangOptions,
    refetchDoJangList,
    clearSelection,

    // 통계 정보
    stats: {
      totalCount: doJangList.length,
      hasSelection: !!selectedDoJangId,
      isReady: !isLoadingDoJang && doJangList.length > 0,
    },
  };
};
